<!-- Solid Queue Dashboard -->
<div class="flex flex-col gap-3 px-4 py-4 pb-16 overflow-y-auto">

  <!-- Header -->
  <div class="flex items-center justify-between">
    <div class="flex items-center gap-3">
      <h1 class="text-2xl font-bold">Solid Queue Kommandozentrale</h1>
      <!-- Auto-Refresh Indikator -->
      <span class="flex items-center gap-1 text-sm text-base-content/60">
        <span class="relative flex h-3 w-3">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3 w-3 bg-success"></span>
        </span>
        Auto-Refresh
      </span>
    </div>
    <button
      class="bm-btn bm-btn-sm bm-btn-ghost"
      (click)="clearFinished()"
      [disabled]="actionInProgress"
    >
      <fa-icon [icon]="faBroom" class="mr-1"></fa-icon>
      Fertige Jobs aufräumen
    </button>
  </div>

  <!-- Ladezustand -->
  @if (loading) {
    <div class="flex justify-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>
  }

  @if (!loading && overview) {

    <!-- Gesamt-Statistik -->
    <div class="flex gap-4 text-sm text-base-content/70">
      <span>Gesamt-Jobs: <strong>{{ overview.total_jobs }}</strong></span>
      <span>Fertige Jobs: <strong>{{ overview.finished_jobs }}</strong></span>
      <span>Aktive Worker: <strong>{{ overview.processes.length }}</strong></span>
    </div>

    <!-- Queue Übersicht Karten -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      @for (queue of overview.queues; track queue.name) {
        <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300 bg-base-100">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-bold text-lg capitalize">{{ queue.name }}</h3>
            <div class="flex items-center gap-2">
              @if (queue.paused) {
                <span class="bm-badge bm-badge-warning bm-badge-sm">Pausiert</span>
              }
              @if (queue.paused) {
                <button
                  class="bm-btn bm-btn-sm bm-btn-ghost"
                  (click)="resumeQueue(queue.name)"
                  [disabled]="actionInProgress"
                  title="Queue fortsetzen"
                >
                  <fa-icon [icon]="faPlay"></fa-icon>
                </button>
              } @else {
                <button
                  class="bm-btn bm-btn-sm bm-btn-ghost"
                  (click)="pauseQueue(queue.name)"
                  [disabled]="actionInProgress"
                  title="Queue pausieren"
                >
                  <fa-icon [icon]="faPause"></fa-icon>
                </button>
              }
            </div>
          </div>

          <div class="grid grid-cols-2 gap-2 text-sm">
            <div class="flex justify-between">
              <span class="text-base-content/60">Wartend</span>
              <span class="font-mono">{{ queue.ready }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-base-content/60">Geplant</span>
              <span class="font-mono">{{ queue.scheduled }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-base-content/60">Blockiert</span>
              <span class="font-mono">{{ queue.blocked }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-base-content/60">Fehlgeschlagen</span>
              <span
                class="font-mono"
                [class]="queue.failed > 0 ? 'bm-badge bm-badge-error bm-badge-sm' : 'bm-badge bm-badge-success bm-badge-sm'"
              >
                {{ queue.failed }}
              </span>
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Worker Prozesse -->
    <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300 bg-base-100">
      <h3 class="font-bold text-lg mb-3">
        <fa-icon [icon]="faServer" class="mr-2"></fa-icon>
        Worker Prozesse
      </h3>

      @if (overview.processes.length === 0) {
        <p class="text-base-content/50 text-sm">Keine aktiven Worker-Prozesse</p>
      } @else {
        <div class="overflow-x-auto">
          <table class="bm-table bm-table-xs w-full">
            <thead>
              <tr>
                <th>Status</th>
                <th>Art</th>
                <th>Name</th>
                <th>Hostname</th>
                <th>PID</th>
                <th>Letzter Heartbeat</th>
              </tr>
            </thead>
            <tbody>
              @for (process of overview.processes; track process.id) {
                <tr>
                  <td>
                    <span
                      class="inline-block w-2.5 h-2.5 rounded-full"
                      [class]="isHeartbeatRecent(process.last_heartbeat_at) ? 'bg-success' : 'bg-error'"
                    ></span>
                  </td>
                  <td>{{ process.kind }}</td>
                  <td class="font-mono text-xs">{{ process.name }}</td>
                  <td>{{ process.hostname }}</td>
                  <td class="font-mono">{{ process.pid }}</td>
                  <td>{{ relativeTime(process.last_heartbeat_at) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    <!-- Fehlgeschlagene Jobs -->
    <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300 bg-base-100">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-lg">
          <fa-icon [icon]="faExclamationTriangle" class="mr-2 text-error"></fa-icon>
          Fehlgeschlagene Jobs
          @if (failedJobsTotal > 0) {
            <span class="bm-badge bm-badge-error bm-badge-sm ml-2">{{ failedJobsTotal }}</span>
          }
        </h3>

        @if (failedJobsTotal > 0) {
          <div class="flex gap-2">
            <button
              class="bm-btn bm-btn-sm bm-btn-primary"
              (click)="retryAllFailed()"
              [disabled]="actionInProgress"
            >
              <fa-icon [icon]="faRedo" class="mr-1"></fa-icon>
              Alle wiederholen
            </button>
            <button
              class="bm-btn bm-btn-sm bm-btn-error"
              (click)="discardAllFailed()"
              [disabled]="actionInProgress"
            >
              <fa-icon [icon]="faTrash" class="mr-1"></fa-icon>
              Alle löschen
            </button>
          </div>
        }
      </div>

      @if (failedJobs.length === 0) {
        <div class="text-center py-8 text-base-content/50">
          <fa-icon [icon]="faCheck" class="text-success text-3xl mb-2"></fa-icon>
          <p>Keine fehlgeschlagenen Jobs</p>
        </div>
      } @else {
        <div class="overflow-x-auto">
          <table class="bm-table bm-table-xs w-full">
            <thead>
              <tr>
                <th>Job-Klasse</th>
                <th>Queue</th>
                <th>Fehler</th>
                <th>Zeitpunkt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              @for (job of failedJobs; track job.id) {
                <tr>
                  <td class="font-mono text-xs">{{ job.job_class }}</td>
                  <td>
                    <span class="bm-badge bm-badge-neutral bm-badge-sm">{{ job.queue_name }}</span>
                  </td>
                  <td class="text-error text-xs max-w-xs truncate" [title]="job.error_message">
                    <strong>{{ job.error_class }}</strong>: {{ truncateError(job.error_message) }}
                  </td>
                  <td class="text-xs whitespace-nowrap">{{ relativeTime(job.created_at) }}</td>
                  <td>
                    <div class="flex gap-1">
                      <button
                        class="bm-btn bm-btn-xs bm-btn-ghost"
                        (click)="retryJob(job.id)"
                        [disabled]="actionInProgress"
                        title="Erneut ausführen"
                      >
                        <fa-icon [icon]="faRedo"></fa-icon>
                      </button>
                      <button
                        class="bm-btn bm-btn-xs bm-btn-ghost text-error"
                        (click)="discardJob(job.id)"
                        [disabled]="actionInProgress"
                        title="Löschen"
                      >
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Paginierung -->
        @if (failedJobsTotalPages > 1) {
          <div class="flex justify-center mt-4 gap-1">
            <button
              class="bm-btn bm-btn-xs bm-btn-ghost"
              [disabled]="failedJobsPage <= 1"
              (click)="goToFailedPage(failedJobsPage - 1)"
            >
              &laquo;
            </button>
            @for (page of paginationPages; track page) {
              <button
                class="bm-btn bm-btn-xs"
                [class.bm-btn-primary]="page === failedJobsPage"
                [class.bm-btn-ghost]="page !== failedJobsPage"
                (click)="goToFailedPage(page)"
              >
                {{ page }}
              </button>
            }
            <button
              class="bm-btn bm-btn-xs bm-btn-ghost"
              [disabled]="failedJobsPage >= failedJobsTotalPages"
              (click)="goToFailedPage(failedJobsPage + 1)"
            >
              &raquo;
            </button>
          </div>
        }
      }
    </div>

    <!-- Wiederkehrende Aufgaben -->
    <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300 bg-base-100">
      <h3 class="font-bold text-lg mb-3">
        <fa-icon [icon]="faClock" class="mr-2"></fa-icon>
        Wiederkehrende Aufgaben
      </h3>

      @if (recurringTasks.length === 0) {
        <p class="text-base-content/50 text-sm">Keine wiederkehrenden Aufgaben konfiguriert</p>
      } @else {
        <div class="overflow-x-auto">
          <table class="bm-table bm-table-xs w-full">
            <thead>
              <tr>
                <th>Key</th>
                <th>Klasse</th>
                <th>Schedule</th>
                <th>Queue</th>
                <th>Letzte Ausführung</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              @for (task of recurringTasks; track task.id) {
                <tr>
                  <td class="font-mono text-xs">{{ task.key }}</td>
                  <td class="font-mono text-xs">{{ task.class_name }}</td>
                  <td>
                    <span class="bm-badge bm-badge-neutral bm-badge-sm" [title]="task.schedule">
                      {{ humanizeCron(task.schedule) }}
                    </span>
                  </td>
                  <td>{{ task.queue_name }}</td>
                  <td class="text-xs">
                    {{ task.last_run_at ? relativeTime(task.last_run_at) : '–' }}
                  </td>
                  <td>
                    <button
                      class="bm-btn bm-btn-xs bm-btn-ghost"
                      (click)="triggerRecurring(task.key)"
                      [disabled]="actionInProgress"
                      title="Jetzt ausführen"
                    >
                      <fa-icon [icon]="faBolt" class="mr-1"></fa-icon>
                      Jetzt ausführen
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  }
</div>
